// frontend/src/components/StaticPageTemplate/index.tsx
import { useEnhancedQuery } from "@uplift-ltd/apollo";
import Image from "next/image";
import { useRouter } from "next/router";
import React from "react";
import { PageIdType } from "__generated__/globalTypes";
import Container from "components/common/Container";
import ErrorPage from "components/common/ErrorPage";
import HtmlContent from "components/common/HtmlContent";
import { FullPageLoading } from "components/common/Loading";
import SectionHeader from "components/common/SectionHeader";
import { usePageMeta } from "hooks/usePageMeta";
import type {
  StaticPageQueryQuery as StaticPageQuery,
  StaticPageQueryQueryVariables as StaticPageQueryVariables,
} from "./__generated__/queries";
import { STATIC_PAGE_QUERY, USER_PAGE_QUERY } from "./queries";
import styles from "./StaticPageTemplate.module.css";

export { STATIC_PAGE_QUERY, USER_PAGE_QUERY };
export type { StaticPageQuery, StaticPageQueryVariables }; // This is the only necessary change
export type StaticPageParams = { slug: string };

const StaticPageTemplate: React.FC = () => {
  const router = useRouter();
  const slug = Array.isArray(router.query.slug)
    ? router.query.slug.join("/")
    : router.query.slug ?? "";
  const pageSlug = slug ? `/${slug}` : "";

  const { data, loading, error } = useEnhancedQuery<StaticPageQuery, StaticPageQueryVariables>(
    STATIC_PAGE_QUERY,
    {
      variables: { id: pageSlug, idType: PageIdType.URI },
    }
  );

  const page = data?.page;
  usePageMeta(page?.title, page?.seo);

  if (loading) return <FullPageLoading />;
  if (error) return <ErrorPage />;
  if (!page) return <ErrorPage />;

  return (
    <>
      {page.heroSection?.title && page.heroSection.summary && (
        <SectionHeader title={page.heroSection.title} summary={page.heroSection.summary} />
      )}
      {page.featuredImage?.node?.mediaItemUrl && (
        <div className="relative h-60">
          <Image src={page.featuredImage.node.mediaItemUrl} layout="fill" objectFit="cover" />
        </div>
      )}
      <Container>
        {page.title && <h1 className={styles.pageTitle}>{page.title}</h1>}
        {page.content && <HtmlContent>{page.content}</HtmlContent>}
      </Container>
    </>
  );
};

export default StaticPageTemplate;
